import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Plus, X, QrCode, Copy, Download, Flag, ArrowCounterClockwise, ChartLineUp, Printer, FileCsv, WhatsappLogo, CheckCircle, Circle, PencilSimple, Camera, MapPin, Lifebuoy, CaretDown, CaretUp } from "@phosphor-icons/react";
import { PRICING, fmtR, curbMarginPerCard } from "../lib/pricing";
import { downloadCsv } from "../lib/csv";
import ScanMap from "../components/ScanMap";
import { PUBLIC_FRONT_URL } from "../lib/publicFront";

const FRONTEND_BASE = PUBLIC_FRONT_URL;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const TABS = [
  { k: "overview", label: "Overview" },
  { k: "codes", label: "Codes" },
  { k: "wine", label: "Wine" },
  { k: "cardholders", label: "Cardholders" },
  { k: "leads", label: "Leads" },
  { k: "scans", label: "Scans" },
  { k: "lost", label: "Lost reports" },
];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [cardholders, setCardholders] = useState([]);
  const [orphanUsers, setOrphanUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [lostReports, setLostReports] = useState([]);
  const [genCount, setGenCount] = useState(10);
  const [genBatchName, setGenBatchName] = useState("");
  const [batches, setBatches] = useState([]);
  const [batchFilter, setBatchFilter] = useState("");
  const [batchesOpen, setBatchesOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewQr, setViewQr] = useState(null);
  const [editCh, setEditCh] = useState(null); // cardholder doc
  const [editForm, setEditForm] = useState({ full_name: "", skills: "", phone: "", location: "", bio: "", picture_url: "" });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [scanSummary, setScanSummary] = useState([]);
  const [scanEvents, setScanEvents] = useState([]);
  const [scanTotal, setScanTotal] = useState(0);
  const [scansLoaded, setScansLoaded] = useState(false);
  const [liveScans, setLiveScans] = useState([]);
  const [recoverCh, setRecoverCh] = useState(null); // cardholder being recovered
  const [recoverOrphan, setRecoverOrphan] = useState(null); // registered user with no card
  const [recoverAccount, setRecoverAccount] = useState(null);
  const [recoverNewEmail, setRecoverNewEmail] = useState("");
  const [recoverNewPw, setRecoverNewPw] = useState("");
  const [recoverMsg, setRecoverMsg] = useState("");
  // Asset sticker pack state — generate retail packs of 10 unbound stickers.
  const [stickerPackCount, setStickerPackCount] = useState(1);
  const [stickerBatchName, setStickerBatchName] = useState("");
  const [stickerBatches, setStickerBatches] = useState([]);
  const [stickerGenResult, setStickerGenResult] = useState(null);
  const [stickerBusy, setStickerBusy] = useState(false);
  const [matrixPackCount, setMatrixPackCount] = useState(1);
  const [matrixStickersPerPack, setMatrixStickersPerPack] = useState(6);
  const [matrixBatchName, setMatrixBatchName] = useState("");
  const [matrixSkus, setMatrixSkus] = useState([]);
  // Empty until /admin/matrix/skus answers — the server owns the default, and a
  // hardcoded guess here silently mints the wrong geometry when a SKU is retired.
  const [matrixSku, setMatrixSku] = useState("");
  const [matrixGenResult, setMatrixGenResult] = useState(null);
  const [matrixBusy, setMatrixBusy] = useState(false);
  const [wineCount, setWineCount] = useState(10);
  const [wineSku, setWineSku] = useState("16mm-v4-21");
  const [wineBatchName, setWineBatchName] = useState("");
  const [wineProducer, setWineProducer] = useState("");
  const [wineBatches, setWineBatches] = useState([]);
  const [wineGenResult, setWineGenResult] = useState(null);
  const [wineBusy, setWineBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c, ch, l, lr] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/codes"),
        api.get("/admin/cardholders"),
        api.get("/admin/leads"),
        api.get("/admin/lost-reports"),
      ]);
      setStats(s.data);
      setCodes(c.data.codes || []);
      setCardholders(ch.data.cardholders || []);
      setOrphanUsers(ch.data.orphan_users || []);
      setLeads(l.data.leads || []);
      setLostReports(lr.data.reports || []);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/codes/batches");
      setBatches(data.batches || []);
    } catch { /* ignore */ }
  }, []);

  const loadStickerBatches = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/assets/batches");
      setStickerBatches(data.batches || []);
    } catch { /* ignore */ }
  }, []);

  const generateStickerPacks = async () => {
    setStickerBusy(true); setStickerGenResult(null);
    try {
      const { data } = await api.post("/admin/assets/generate-packs", {
        pack_count: Number(stickerPackCount) || 1,
        batch_name: stickerBatchName.trim() || undefined,
      });
      setStickerGenResult(data);
      setStickerBatchName("");
      await loadStickerBatches();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setStickerBusy(false); }
  };

  const loadMatrixSkus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/matrix/skus");
      setMatrixSkus(data.skus || []);
      if (data.default) setMatrixSku(data.default);
    } catch { /* ignore — mint falls back to the server default */ }
  }, []);

  const generateMatrixPacks = async () => {
    setMatrixBusy(true); setMatrixGenResult(null); setErr("");
    try {
      const { data } = await api.post("/admin/assets/generate-matrix-packs", {
        pack_count: Number(matrixPackCount) || 1,
        stickers_per_pack: Number(matrixStickersPerPack) || 6,
        batch_name: matrixBatchName.trim() || undefined,
        sku: matrixSku,
        kid: "curb-1",
      });
      setMatrixGenResult(data);
      setMatrixBatchName("");
      await loadStickerBatches();
      toast.success(`Minted ${data.created} Twin-Matrix stickers`);
    } catch (e) {
      setErr(formatApiError(e));
      toast.error(formatApiError(e));
    } finally { setMatrixBusy(false); }
  };

  const selectedMatrixSku = matrixSkus.find((s) => s.name === matrixSku) || null;

  useEffect(() => { if (user && user.role === "super_admin") { load(); loadBatches(); loadStickerBatches(); loadMatrixSkus(); } }, [user, load, loadBatches, loadStickerBatches, loadMatrixSkus]);

  const loadScans = useCallback(async () => {
    try {
      const [sum, ev] = await Promise.all([
        api.get("/admin/scan-events/summary"),
        api.get("/admin/scan-events?limit=200"),
      ]);
      setScanSummary(sum.data.summary || []);
      setScanTotal(sum.data.total_scans || 0);
      setScanEvents(ev.data.events || []);
      setScansLoaded(true);
    } catch (e) { setErr(formatApiError(e)); }
  }, []);

  useEffect(() => {
    if (tab === "scans" && user?.role === "super_admin" && !scansLoaded) loadScans();
  }, [tab, user, scansLoaded, loadScans]);

  const loadWineBatches = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/wine/batches");
      setWineBatches(data.batches || []);
    } catch { /* ignore */ }
  }, []);

  const downloadWinePdf = async (batchName) => {
    try {
      const res = await api.get(
        `/admin/wine/batches/${encodeURIComponent(batchName)}/pdf`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wine-${batchName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const generateWine = async () => {
    setWineBusy(true); setWineGenResult(null); setErr("");
    try {
      const { data } = await api.post("/admin/wine/generate", {
        count: Number(wineCount) || 1,
        batch_name: wineBatchName.trim() || undefined,
        producer: wineProducer.trim(),
        sku: wineSku || "16mm-v4-21",
      });
      setWineGenResult(data);
      setWineBatchName("");
      await loadWineBatches();
      toast.success(`Minted ${data.count} bottle stickers`);
    } catch (e) {
      setErr(formatApiError(e));
      toast.error(formatApiError(e));
    } finally { setWineBusy(false); }
  };

  useEffect(() => {
    if (tab === "wine" && user?.role === "super_admin") loadWineBatches();
  }, [tab, user, loadWineBatches]);

  // Live scan ticker for the Overview tab. Polls every 30s.
  useEffect(() => {
    if (!user || user.role !== "super_admin") return undefined;
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const { data } = await api.get("/admin/scan-events?limit=8");
        if (!cancelled) setLiveScans(data.events || []);
      } catch { /* ignore — keep last value */ }
    };
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  if (loading) return <div><Nav /><div className="p-8 label-tag">Loading…</div></div>;
  if (!user || user.role !== "super_admin") {
    window.location.href = "/admin/login";
    return null;
  }

  const generate = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/admin/codes/generate", { count: Number(genCount), batch_name: genBatchName.trim() || undefined });
      setGenBatchName("");
      await load();
      await loadBatches();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const openRecover = async (c) => {
    setRecoverCh(c);
    setRecoverOrphan(null);
    setRecoverAccount(null);
    setRecoverNewEmail("");
    setRecoverNewPw("");
    setRecoverMsg("");
    try {
      const { data } = await api.get(`/admin/cardholder/${c.id}/account`);
      setRecoverAccount(data);
      if (data.account) setRecoverNewEmail(data.account.email);
    } catch (e) { setRecoverMsg(formatApiError(e)); }
  };

  const openRecoverOrphan = (u) => {
    setRecoverCh(null);
    setRecoverOrphan(u);
    setRecoverAccount({ account: u });
    setRecoverNewEmail(u.email || "");
    setRecoverNewPw("");
    setRecoverMsg("");
  };

  const closeRecover = () => {
    setRecoverCh(null);
    setRecoverOrphan(null);
  };

  const saveRecoverEmail = async () => {
    setRecoverMsg("");
    try {
      if (recoverOrphan) {
        const { data } = await api.post(`/admin/users/${recoverOrphan.id}/change-email`, { new_email: recoverNewEmail.trim() });
        setRecoverMsg(`✓ Email updated to ${data.email}`);
        setRecoverOrphan({ ...recoverOrphan, email: data.email });
        setRecoverAccount({ account: { ...recoverOrphan, email: data.email } });
        await load();
        return;
      }
      const { data } = await api.post(`/admin/cardholder/${recoverCh.id}/change-email`, { new_email: recoverNewEmail.trim() });
      setRecoverMsg(`✓ Email updated to ${data.email}`);
      const refresh = await api.get(`/admin/cardholder/${recoverCh.id}/account`);
      setRecoverAccount(refresh.data);
      await load();
    } catch (e) { setRecoverMsg(formatApiError(e)); }
  };

  const saveRecoverPassword = async () => {
    setRecoverMsg("");
    if (recoverNewPw.length < 6) return setRecoverMsg("Password must be at least 6 characters");
    try {
      if (recoverOrphan) {
        await api.post(`/admin/users/${recoverOrphan.id}/reset-password`, { new_password: recoverNewPw });
        setRecoverMsg(`✓ Password reset. Tell them their new password is: ${recoverNewPw}`);
        return;
      }
      await api.post(`/admin/cardholder/${recoverCh.id}/reset-password`, { new_password: recoverNewPw });
      setRecoverMsg(`✓ Password reset. Tell the cardholder their new password is: ${recoverNewPw}`);
    } catch (e) { setRecoverMsg(formatApiError(e)); }
  };

  const toggleSel = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const toggleActive = async (ch, deactivate) => {
    try {
      await api.post(`/admin/cardholder/${ch.id}/${deactivate ? "deactivate" : "restore"}`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const printSelected = () => {
    const ids = [...selected];
    if (ids.length === 0) {
      window.open("/admin/print", "_blank");
    } else {
      window.open(`/admin/print?ids=${ids.join(",")}`, "_blank");
    }
  };

  const printerPdf = () => {
    const ids = [...selected];
    if (ids.length) {
      window.open(`/admin/print-cards?ids=${ids.join(",")}`, "_blank");
      return;
    }
    const params = new URLSearchParams({ status: "unassigned" });
    if (batchFilter) params.set("batch", batchFilter);
    window.open(`/admin/print-cards?${params.toString()}`, "_blank");
  };

  const exportCsv = (kind) => {
    if (kind === "cardholders") {
      downloadCsv("curbcrew-cardholders", cardholders.map((c) => ({
        code: c.code, full_name: c.full_name, skills: c.skills,
        location: c.location || "", phone: c.phone || "",
        owner_email: c.owner_email || "", has_login: !!c.has_login,
        is_active: c.is_active !== false, lost_reported: !!c.lost_reported,
        created_at: c.created_at,
      })));
      if (orphanUsers.length) {
        downloadCsv("curbcrew-registered-no-card", orphanUsers.map((u) => ({
          email: u.email, name: u.name || "", phone: u.phone || "",
          created_at: u.created_at,
        })));
      }
    } else if (kind === "leads") {
      downloadCsv("curbcrew-leads", leads.map((l) => ({
        when: l.created_at, code: l.code, cardholder: l.cardholder_name,
        interested_name: l.interested_name, interested_phone: l.interested_phone,
        message: l.message || "",
        contacted: !!l.contacted, contacted_at: l.contacted_at || "",
      })));
    } else if (kind === "codes") {
      downloadCsv("curbcrew-codes", codes.map((c) => ({
        code: c.code, status: c.status,
        created_at: c.created_at, activated_at: c.activated_at || "",
      })));
    }
  };

  const filteredCodes = batchFilter ? codes.filter((c) => c.batch_name === batchFilter) : codes;

  // Convert SA phone "0821234567" -> "27821234567" for wa.me links.
  const toWaNumber = (phone) => {
    if (!phone) return "";
    const digits = String(phone).replace(/\D/g, "");
    if (digits.startsWith("27")) return digits;
    if (digits.startsWith("0")) return "27" + digits.slice(1);
    return digits;
  };

  const forwardLeadWhatsApp = (l) => {
    const wa = toWaNumber(l.cardholder_phone);
    if (!wa) {
      setErr("This cardholder has no phone on file — cannot forward via WhatsApp.");
      return;
    }
    const lines = [
      `Hi ${l.cardholder_name || "there"}, you got a new lead from your Curb Crew card #${l.code}.`,
      "",
      `Name: ${l.interested_name}`,
      `Phone: ${l.interested_phone}`,
    ];
    if (l.message) {
      lines.push(`Message: ${l.message}`);
    }
    lines.push("", "Reply to them directly. Good luck!");
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${wa}?text=${text}`, "_blank", "noopener");
  };

  const toggleContacted = async (leadId) => {
    try {
      const r = await api.post(`/admin/leads/${leadId}/contacted`);
      setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, contacted: r.data.contacted } : l));
      // Refresh stats so the pending count updates on the overview
      try {
        const s = await api.get("/admin/stats");
        setStats(s.data);
      } catch {}
    } catch (e) { setErr(formatApiError(e)); }
  };

  const pendingLeads = leads.filter((l) => !l.contacted).length;

  const openEditCh = (c) => {
    setEditForm({
      full_name: c.full_name || "",
      skills: (c.skills || []).join(", "),
      phone: c.phone || "",
      location: c.location || "",
      bio: c.bio || "",
      picture_url: c.picture_url || "",
    });
    setErr("");
    setEditCh(c);
  };

  const handleAdminPhotoUpload = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditForm((prev) => ({ ...prev, picture_url: data.url }));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploadingPhoto(false); }
  };

  const submitEditCh = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^0\d{9}$/.test(editForm.phone)) return setErr("Phone must be 10 digits starting with 0");
    if (!editForm.full_name.trim()) return setErr("Name is required");
    const skills = editForm.skills.split(",").map((s) => s.trim()).filter(Boolean);
    if (skills.length === 0) return setErr("Add at least one skill");
    if (!editForm.picture_url) return setErr("A photo is required");
    try {
      await api.patch(`/admin/cardholder/${editCh.id}`, {
        full_name: editForm.full_name,
        skills,
        phone: editForm.phone,
        location: editForm.location,
        bio: editForm.bio,
        picture_url: editForm.picture_url,
      });
      setEditCh(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const submitFactoryReset = async (e) => {
    e.preventDefault();
    setErr("");
    if (resetConfirm !== "RESET CURBCREW") {
      return setErr("Type the confirmation phrase exactly: RESET CURBCREW");
    }
    try {
      const { data } = await api.post("/admin/factory-reset", { confirm_phrase: resetConfirm });
      setShowReset(false);
      setResetConfirm("");
      await load();
      const leftoverAgents = data.deleted?.agents ? `\n- ${data.deleted.agents} leftover agents` : "";
      window.alert(`Factory reset complete.\n\nDeleted:\n- ${data.deleted.codes} codes\n- ${data.deleted.cardholders} cardholders\n- ${data.deleted.leads} leads\n- ${data.deleted.lost_reports} lost reports${leftoverAgents}\n\nAdmin account preserved.`);
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="label-tag text-[color:var(--cc-yellow)]">Super admin</div>
              <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mt-2">Control room</h1>
            </div>
            {/* Curb Checkpoint quick-jump — always visible regardless of active tab */}
            <div className="flex flex-wrap gap-2" data-testid="admin-cc-shortcuts">
              <Link
                to="/cc/admin/mints"
                className="label-tag bg-[color:var(--cc-yellow)] text-black px-4 py-3 brutal-btn inline-flex items-center gap-2"
                data-testid="admin-cc-mints-link"
                title="Mint a batch of Curb Checkpoint stickers · download print-ready PDF"
              >
                ✦ Curb Checkpoint mints
              </Link>
              <Link
                to="/cc/admin"
                className="label-tag border-2 border-white text-white px-4 py-3 brutal-btn inline-flex items-center gap-2"
                data-testid="admin-cc-admin-link"
              >
                Checkpoint shares
              </Link>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-0 border-l border-t border-white">
            {TABS.map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`label-tag px-5 py-3 border-r border-b border-white ${tab === t.k ? "bg-[color:var(--cc-yellow)] text-black" : "hover:bg-white/10"}`}
                data-testid={`tab-${t.k}`}
              >
                {t.label}
                {t.k === "leads" && pendingLeads > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1 bg-red-600 text-white text-[11px] font-bold rounded-full" data-testid="leads-pending-badge">
                    {pendingLeads}
                  </span>
                )}
                {t.k === "cardholders" && orphanUsers.length > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1 bg-[color:var(--cc-yellow)] text-black text-[11px] font-bold border border-black" data-testid="orphans-badge">
                    {orphanUsers.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {err && <div className="label-tag text-red-600 mb-4" data-testid="admin-error">{err}</div>}

        {tab === "wine" && (
          <div className="brutal-border p-5 bg-white" data-testid="wine-stickers-section">
            <div className="font-display font-black text-xl">Bottle stickers</div>
            <div className="label-tag text-neutral-600 mt-1">
              Twin-Matrix stickers. Print the PDF at 100%. The nested QR opens the bottle page; the first scan registers it.
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_1fr_auto] gap-3 items-end">
              <div>
                <label className="label-tag">Sticker size</label>
                {matrixSkus.length > 0 ? (
                  <select
                    value={wineSku}
                    onChange={(e) => setWineSku(e.target.value)}
                    className="mt-1 px-3 py-2 border border-black outline-none w-full bg-white"
                    data-testid="wine-sku"
                  >
                    {matrixSkus.map((s) => (
                      <option key={s.name} value={s.name}>
                        {`${s.sticker_mm}mm · ${s.symbol === "datamatrix" ? "Data Matrix" : `V${s.version}`} · ${s.module_mm}mm`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="mt-1 px-3 py-2 border border-black text-neutral-500 label-tag">16mm-v4-21</div>
                )}
              </div>
              <div>
                <label className="label-tag">Stickers</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={wineCount}
                  onChange={(e) => setWineCount(e.target.value)}
                  className="mt-1 px-3 py-2 border border-black outline-none w-24"
                  data-testid="wine-count"
                />
              </div>
              <div>
                <label className="label-tag">Producer</label>
                <input
                  type="text"
                  value={wineProducer}
                  onChange={(e) => setWineProducer(e.target.value)}
                  placeholder="Estate or distillery"
                  className="mt-1 px-3 py-2 border border-black outline-none w-full"
                  data-testid="wine-producer"
                />
              </div>
              <div>
                <label className="label-tag">Batch name <span className="text-neutral-500">(optional)</span></label>
                <input
                  type="text"
                  value={wineBatchName}
                  onChange={(e) => setWineBatchName(e.target.value)}
                  placeholder="2026 Cabernet neck labels"
                  className="mt-1 px-3 py-2 border border-black outline-none w-full"
                  data-testid="wine-batch-name"
                />
              </div>
              <button
                onClick={generateWine}
                disabled={wineBusy}
                className="bg-black text-white label-tag px-4 py-3 brutal-btn disabled:opacity-50"
                data-testid="wine-generate-btn"
              >
                {wineBusy ? "Minting…" : `Generate ${Number(wineCount) || 1}`}
              </button>
            </div>
            {wineGenResult && (
              <div className="mt-4 border-2 border-black bg-[color:var(--cc-yellow)] p-3" data-testid="wine-gen-result">
                <div className="label-tag">Just minted</div>
                <div className="font-display font-black text-lg mt-0.5">{wineGenResult.batch_name}</div>
                <div className="text-sm mt-1">{wineGenResult.producer || "No producer"} · {wineGenResult.count} Twin-Matrix · {wineGenResult.sku}</div>
                <button
                  type="button"
                  onClick={() => downloadWinePdf(wineGenResult.batch_name)}
                  className="mt-2 label-tag px-2 py-1 border border-black bg-white brutal-btn"
                  data-testid="wine-print-fresh"
                >
                  Download PDF
                </button>
              </div>
            )}
            {wineBatches.length > 0 && (
              <div className="mt-5">
                <div className="label-tag mb-2">Batches</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {wineBatches.map((b) => (
                    <div key={b.batch_name} className="border border-black p-3 bg-neutral-50" data-testid={`wine-batch-${b.batch_name}`}>
                      <div className="font-mono-cc text-sm break-words">{b.batch_name}</div>
                      <div className="label-tag text-neutral-600 mt-1">
                        {b.producer || "No producer"} · {b.bound} of {b.total} registered
                      </div>
                      <button
                        type="button"
                        onClick={() => downloadWinePdf(b.batch_name)}
                        className="mt-2 label-tag px-2 py-1 border border-black bg-[color:var(--cc-yellow)] brutal-btn"
                        data-testid={`wine-batch-print-${b.batch_name}`}
                      >
                        Download PDF
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "overview" && stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-black" data-testid="overview-grid">
              {[
                { k: "Total codes", v: stats.total_codes },
                { k: "Unassigned", v: stats.unassigned_codes },
                { k: "Assigned", v: stats.assigned_codes },
                { k: "Activated", v: stats.activated_codes },
                { k: "Cardholders", v: stats.total_cardholders },
                { k: "Registered, no card", v: stats.orphan_users ?? orphanUsers.length, highlight: (stats.orphan_users ?? orphanUsers.length) > 0 },
                { k: "Leads captured", v: stats.total_leads },
                { k: "Leads pending", v: stats.pending_leads ?? 0, highlight: (stats.pending_leads ?? 0) > 0 },
              ].map((s, i) => (
                <div key={i} className={`p-5 border-r border-b border-black ${s.highlight ? "bg-[color:var(--cc-yellow)]" : "bg-white"}`}>
                  <div className="label-tag text-neutral-500">{s.k}</div>
                  <div className="font-display font-black text-3xl mt-1">{s.v}</div>
                </div>
              ))}
            </div>

            {/* Live scan ticker */}
            <div className="mt-8 bg-white border border-black p-5" data-testid="live-scan-panel">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="label-tag flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-600" />
                  </span>
                  Live scan activity
                </div>
                <button onClick={() => setTab("scans")} className="label-tag underline text-neutral-600 hover:text-black" data-testid="live-view-all">
                  View all →
                </button>
              </div>
              <div className="mt-3 divide-y divide-neutral-200" data-testid="live-scan-list">
                {liveScans.slice(0, 5).map((e) => {
                  const ch = cardholders.find((c) => c.code === e.code);
                  const where = [e.city, e.country].filter(Boolean).join(", ");
                  return (
                    <div key={e.id} className="py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`live-scan-row-${e.id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">
                          {ch?.full_name || <span className="text-neutral-500">Unknown cardholder</span>}
                          <span className="ml-2 font-mono-cc text-xs text-neutral-500">#{e.code}</span>
                        </div>
                        <div className="text-xs text-neutral-600 mt-0.5">
                          scanned {where ? `from ${where}` : <span className="italic">location pending</span>}
                        </div>
                      </div>
                      <div className="label-tag text-neutral-500 whitespace-nowrap">{timeAgo(e.created_at)}</div>
                    </div>
                  );
                })}
                {liveScans.length === 0 && (
                  <div className="py-3 label-tag text-neutral-500">No scans yet — public profile views will appear here in real time.</div>
                )}
              </div>
            </div>

            <div className="mt-8 bg-black text-white border border-black p-6" data-testid="margin-panel">
              <div className="flex items-center gap-2 label-tag text-[color:var(--cc-yellow)]">
                <ChartLineUp size={16} weight="bold" /> Margin · admin only
              </div>
              <div className="mt-2 text-sm text-neutral-300">Per-card economics, ignoring shipping. Hardware cost is hidden from cardholders.</div>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-white/30">
                {[
                  { k: "Cardholder pays", v: fmtR(PRICING.cardholder_price) },
                  { k: "Wholesale / card", v: fmtR(PRICING.pack_price / PRICING.pack_size) },
                  { k: "Hardware cost", v: fmtR(PRICING.hardware_cost_per_card) },
                  { k: "Curb net / card", v: fmtR(curbMarginPerCard()), highlight: true },
                ].map((s) => (
                  <div key={s.k} className={`p-4 border-r border-b border-white/30 ${s.highlight ? "bg-[color:var(--cc-yellow)] text-black" : ""}`}>
                    <div className="label-tag opacity-70">{s.k}</div>
                    <div className="font-display font-black text-2xl mt-1 tabular-nums">{s.v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-white/30">
                {[
                  { k: "Lifetime cardholder revenue", v: fmtR(stats.activated_codes * PRICING.cardholder_price) },
                  { k: "Lifetime Curb net", v: fmtR(stats.activated_codes * curbMarginPerCard()), highlight: true },
                ].map((s) => (
                  <div key={s.k} className={`p-4 border-r border-b border-white/30 ${s.highlight ? "bg-[color:var(--cc-yellow)] text-black" : ""}`}>
                    <div className="label-tag opacity-70">{s.k}</div>
                    <div className="font-display font-black text-2xl mt-1 tabular-nums">{s.v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 text-xs text-neutral-400">
                Shipping ({fmtR(PRICING.shipping_fee)} PostNet) is recovered separately on orders below {PRICING.free_shipping_threshold} codes; included on bulk orders.
              </div>
            </div>

            <div className="mt-8 border-2 border-red-600 bg-white p-6" data-testid="danger-zone">
              <div className="label-tag text-red-600">Danger zone</div>
              <div className="font-display font-black text-2xl mt-1">Reset all business data</div>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
                Deletes every code, cardholder, lead and lost report from this database.
                Your admin account stays. Use this once at launch to clear out test data — it cannot be undone.
              </p>
              <button
                onClick={() => { setShowReset(true); setResetConfirm(""); }}
                className="mt-4 label-tag px-5 py-3 bg-red-600 text-white border border-red-700 brutal-btn"
                data-testid="open-factory-reset"
              >
                Reset everything
              </button>
            </div>
          </>
        )}

        {tab === "codes" && (
          <div className="space-y-6">
            <div className="brutal-border p-5 bg-white" data-testid="generate-section">
              <div className="font-display font-black text-xl">Generate codes</div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <div>
                  <label className="label-tag text-neutral-500">How many?</label>
                  <input type="number" min={1} max={500} value={genCount} onChange={(e) => setGenCount(e.target.value)} className="mt-1 w-28 px-3 py-2 border border-black outline-none font-mono-cc block" data-testid="gen-count" />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <label className="label-tag text-neutral-500">Batch name <span className="text-neutral-400">(optional)</span></label>
                  <input
                    type="text"
                    placeholder="e.g. Batch 001 — Pep Builders pilot"
                    value={genBatchName}
                    onChange={(e) => setGenBatchName(e.target.value)}
                    maxLength={60}
                    className="mt-1 w-full px-3 py-2 border border-black outline-none"
                    data-testid="gen-batch-name"
                  />
                  <div className="label-tag text-neutral-500 mt-1">Leave blank to auto-name: "Batch 002 · 500 cards · 2026-02-15"</div>
                </div>
                <button onClick={generate} disabled={busy} className="bg-[color:var(--cc-yellow)] label-tag px-4 py-3 brutal-btn flex items-center gap-2 self-end" data-testid="gen-submit">
                  <Plus size={14} weight="bold" /> Generate
                </button>
              </div>
            </div>

            {/* Batches summary — collapsed by default (grid is huge) */}
            {batches.length > 0 && (
              <div className="brutal-border p-5 bg-white" data-testid="batches-section">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-display font-black text-xl">Print batches</div>
                    <div className="label-tag text-neutral-600 mt-0.5">
                      {batches.length} batch{batches.length === 1 ? "" : "es"}
                      {batchFilter ? <> · filtered: <strong>{batchFilter}</strong></> : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {batchesOpen && (
                      <select value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)} className="px-3 py-2 border border-black outline-none label-tag" data-testid="batch-filter">
                        <option value="">All batches</option>
                        {batches.map((b) => <option key={b.batch_name} value={b.batch_name}>{b.batch_name} ({b.count})</option>)}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => setBatchesOpen((o) => !o)}
                      className="label-tag px-4 py-2 border border-black bg-[color:var(--cc-yellow)] brutal-btn inline-flex items-center gap-2"
                      data-testid="batches-toggle"
                      aria-expanded={batchesOpen}
                    >
                      {batchesOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                      {batchesOpen ? "Hide batches" : "Show batches"}
                    </button>
                  </div>
                </div>
                {batchesOpen && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="batches-grid">
                    {batches.map((b) => (
                      <div key={b.batch_name} className={`border border-black p-3 ${batchFilter === b.batch_name ? "bg-[color:var(--cc-yellow)]" : "bg-neutral-50"}`} data-testid={`batch-${b.batch_name}`}>
                        <div className="font-mono-cc text-sm break-words">{b.batch_name}</div>
                        <div className="mt-1 label-tag text-neutral-600">
                          {b.count} total · {b.unassigned} to print · {b.activated} activated
                        </div>
                        <div className="mt-2 flex gap-2 flex-wrap">
                          <button onClick={() => setBatchFilter(b.batch_name)} className="label-tag px-2 py-1 border border-black bg-white brutal-btn">Filter</button>
                          <button
                            onClick={() => window.open(`/admin/print-cards?status=unassigned&batch=${encodeURIComponent(b.batch_name)}`, "_blank")}
                            className="label-tag px-2 py-1 border border-black bg-black text-white brutal-btn"
                            data-testid={`batch-print-${b.batch_name}`}
                          >
                            Printer PDF
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Asset sticker packs — generate batches of unbound stickers
                in packs of 10, sold next to primary cards. Cardholders
                claim them via the public scan -> claim flow. */}
            <div className="brutal-border p-5 bg-white" data-testid="sticker-packs-section">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-display font-black text-xl">Asset sticker packs</div>
                  <div className="label-tag text-neutral-600 mt-1">Each pack = 10 unbound QR stickers. Cardholders claim them after purchase.</div>
                </div>
                <Link
                  to="/cc/admin/mints"
                  className="label-tag bg-black text-white px-3 py-2 brutal-btn"
                  data-testid="admin-cc-mints-link"
                >
                  → Curb Checkpoint mints
                </Link>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-3 items-end">
                <div>
                  <label className="label-tag">Packs to generate</label>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    value={stickerPackCount}
                    onChange={(e) => setStickerPackCount(e.target.value)}
                    className="mt-1 px-3 py-2 border border-black outline-none w-24"
                    data-testid="sticker-pack-count"
                  />
                </div>
                <div>
                  <label className="label-tag">Batch name <span className="text-neutral-500">(optional — auto-named if blank)</span></label>
                  <input
                    type="text"
                    value={stickerBatchName}
                    onChange={(e) => setStickerBatchName(e.target.value)}
                    placeholder="Plumber retail pack 2026-06"
                    className="mt-1 px-3 py-2 border border-black outline-none w-full"
                    data-testid="sticker-batch-name"
                  />
                </div>
                <button
                  onClick={generateStickerPacks}
                  disabled={stickerBusy}
                  className="bg-black text-white label-tag px-4 py-3 brutal-btn disabled:opacity-50"
                  data-testid="sticker-generate-btn"
                >
                  {stickerBusy ? "Minting…" : `Generate ${Number(stickerPackCount) || 1} pack(s) · ${(Number(stickerPackCount) || 1) * 10} codes`}
                </button>
              </div>

              {stickerGenResult && (
                <div className="mt-4 border-2 border-black bg-[color:var(--cc-yellow)] p-3" data-testid="sticker-gen-result">
                  <div className="label-tag">Just minted</div>
                  <div className="font-display font-black text-lg mt-0.5">{stickerGenResult.batch_name}</div>
                  <div className="text-sm mt-1">
                    <strong>{stickerGenResult.pack_count}</strong> pack(s) · <strong>{stickerGenResult.total_codes}</strong> codes. First 6 codes:
                  </div>
                  <div className="mt-1 font-mono-cc text-xs flex flex-wrap gap-1">
                    {stickerGenResult.codes.slice(0, 6).map((c) => (
                      <span key={c.asset_code} className="px-2 py-0.5 bg-black text-white">{c.asset_code}</span>
                    ))}
                  </div>
                </div>
              )}

              {stickerBatches.length > 0 && (
                <div className="mt-5">
                  <div className="label-tag mb-2">Existing sticker batches</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {stickerBatches.map((b) => (
                      <div key={b.batch_name} className="border border-black p-3 bg-neutral-50" data-testid={`sticker-batch-${b.batch_name}`}>
                        <div className="font-mono-cc text-sm break-words">{b.batch_name}</div>
                        <div className="label-tag text-neutral-600 mt-1">
                          {b.claimed} of {b.total} stickers claimed · {b.pack_count} pack{b.pack_count === 1 ? "" : "s"}
                        </div>
                        <div className="mt-2 flex gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => window.open(`/admin/print-stickers/${encodeURIComponent(b.batch_name)}`, "_blank")}
                            className="label-tag px-2 py-1 border border-black bg-[color:var(--cc-yellow)] brutal-btn"
                            data-testid={`sticker-batch-print-${b.batch_name}`}
                          >
                            Print sheet
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const { data } = await api.get(`/admin/assets/batch/${encodeURIComponent(b.batch_name)}`);
                                const lines = data.codes.map((c) => `Pack ${c.pack_number || "-"}\tSlot ${c.pack_slot || "-"}\t${c.asset_code}\t${c.status === "bound" ? "claimed by " + (c.card_code || "?") : "unbound"}`).join("\n");
                                const blob = new Blob([`Batch: ${data.batch_name}\nPack size: ${data.pack_size}\n\n${lines}`], { type: "text/plain" });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a"); a.href = url; a.download = `${b.batch_name}.txt`; a.click();
                                URL.revokeObjectURL(url);
                              } catch (e) { toast.error(formatApiError(e)); }
                            }}
                            className="label-tag px-2 py-1 border border-black bg-white brutal-btn"
                            data-testid={`sticker-batch-export-${b.batch_name}`}
                          >
                            Export codes
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="brutal-border bg-white p-4 mt-4" data-testid="matrix-packs-panel">
              <div className="label-tag">Twin-Matrix packs</div>
              <h3 className="font-display font-black text-xl mt-1">Cryptographic stickers</h3>
              <p className="text-sm text-neutral-600 mt-1">
                Curbseal-signed V4 outer + nested pointer to <code className="font-mono-cc">/matrix/&#123;code&#125;</code>. Requires <code className="font-mono-cc">MATRIX_COSE_PRIVATE_KEY_PEM</code> on the admin API.
              </p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div>
                  <label className="label-tag">Sticker size</label>
                  {matrixSkus.length > 0 ? (
                    <select
                      value={matrixSku}
                      onChange={(e) => setMatrixSku(e.target.value)}
                      className="mt-1 px-3 py-2 border border-black outline-none w-full bg-white"
                      data-testid="matrix-sku"
                    >
                      {matrixSkus.map((s) => (
                        <option key={s.name} value={s.name}>
                          {`${s.sticker_mm}mm · ${s.symbol === "datamatrix" ? "Data Matrix" : `V${s.version}`} · ${s.module_mm}mm modules${s.field_tested ? "" : " · UNTESTED"}`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="mt-1 px-3 py-2 border border-black text-neutral-500 label-tag">Loading…</div>
                  )}
                </div>
                <div>
                  <label className="label-tag">Packs</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={matrixPackCount}
                    onChange={(e) => setMatrixPackCount(e.target.value)}
                    className="mt-1 px-3 py-2 border border-black outline-none w-24"
                    data-testid="matrix-pack-count"
                  />
                </div>
                <div>
                  <label className="label-tag">Stickers / pack</label>
                  <input
                    type="number"
                    min={1}
                    max={12}
                    value={matrixStickersPerPack}
                    onChange={(e) => setMatrixStickersPerPack(e.target.value)}
                    className="mt-1 px-3 py-2 border border-black outline-none w-24"
                    data-testid="matrix-stickers-per-pack"
                  />
                </div>
                <div>
                  <label className="label-tag">Batch name <span className="text-neutral-500">(optional)</span></label>
                  <input
                    type="text"
                    value={matrixBatchName}
                    onChange={(e) => setMatrixBatchName(e.target.value)}
                    placeholder="Matrix retail 2026-08"
                    className="mt-1 px-3 py-2 border border-black outline-none w-full"
                    data-testid="matrix-batch-name"
                  />
                </div>
              </div>
              {selectedMatrixSku && (
                <div className="mt-3 border border-black bg-[color:var(--cc-bg-2)] p-3" data-testid="matrix-sku-spec">
                  <div className="label-tag">{selectedMatrixSku.name}</div>
                  <div className="text-sm mt-1 font-mono-cc">
                    {selectedMatrixSku.symbol === "datamatrix"
                      ? `DM ECC200 · ${selectedMatrixSku.grid}×${selectedMatrixSku.grid} grid · `
                      : `V${selectedMatrixSku.version} · ${selectedMatrixSku.grid}×${selectedMatrixSku.grid} grid · `}
                    {`${selectedMatrixSku.module_mm}mm modules · cut ${selectedMatrixSku.cut_rows}×${selectedMatrixSku.cut_cols} · `}
                    {`void ${selectedMatrixSku.void_mm}mm · nested ${selectedMatrixSku.nested_mm}mm · `}
                    {selectedMatrixSku.symbol === "datamatrix" ? "scan → /dmx/" : "scan → /matrix/"}
                  </div>
                  {!selectedMatrixSku.field_tested && (
                    <div className="text-sm mt-2 font-bold" data-testid="matrix-sku-untested">
                      Never printed or scanned on a real handset. Mint a batch, print at 100%, and
                      {selectedMatrixSku.symbol === "datamatrix"
                        ? " confirm the nested QR opens /dmx/ and the reconstituted Data Matrix reads before ordering a die at this size."
                        : " confirm both QRs read before ordering a die at this size."}
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-neutral-500 mt-2">
                The nested pointer prints at 10mm on every size — that is the stock-camera focus floor. Smaller stickers pay for it with a deeper cut, not a smaller pointer. Print the PDF at 100%.
              </p>
              <button
                type="button"
                onClick={generateMatrixPacks}
                disabled={matrixBusy}
                className="mt-3 bg-black text-white label-tag px-4 py-3 brutal-btn disabled:opacity-50"
                data-testid="matrix-generate-btn"
              >
                {matrixBusy
                  ? "Minting…"
                  : `Generate ${Number(matrixPackCount) || 1}×${Number(matrixStickersPerPack) || 6} Twin-Matrix`}
              </button>
              {matrixGenResult && (
                <div className="mt-4 border-2 border-black bg-[color:var(--cc-yellow)] p-3" data-testid="matrix-gen-result">
                  <div className="label-tag">Just minted</div>
                  <div className="font-display font-black text-lg mt-0.5">{matrixGenResult.batch_name}</div>
                  <div className="text-sm mt-1">
                    <strong>{matrixGenResult.created}</strong> stickers · kid {matrixGenResult.kid} · {matrixGenResult.sku}
                  </div>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="label-tag px-2 py-1 border border-black bg-white brutal-btn"
                      onClick={async () => {
                        try {
                          const res = await api.get(
                            `/admin/assets/matrix-batches/${encodeURIComponent(matrixGenResult.batch_name)}/pdf`,
                            { responseType: "blob" }
                          );
                          const url = URL.createObjectURL(res.data);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `matrix-${matrixGenResult.batch_name}.pdf`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch (e) {
                          toast.error(formatApiError(e));
                        }
                      }}
                    >
                      Download PDF
                    </button>
                  </div>
                  <div className="mt-2 font-mono-cc text-xs flex flex-wrap gap-1">
                    {(matrixGenResult.asset_codes || []).slice(0, 8).map((code) => (
                      <span key={code} className="px-2 py-0.5 bg-black text-white">{code}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="brutal-border p-5 bg-white" data-testid="codes-actions">
              <div className="font-display font-black text-xl">Print &amp; export</div>
              <div className="mt-3 flex flex-wrap gap-3 items-center">
                <button onClick={printSelected} className="bg-[color:var(--cc-yellow)] label-tag px-4 py-3 brutal-btn flex items-center gap-2" data-testid="print-sheet-btn">
                  <Printer size={14} weight="bold" /> Preview sheet (A4, 8/page)
                </button>
                <button onClick={printerPdf} className="bg-black text-white label-tag px-4 py-3 brutal-btn flex items-center gap-2" data-testid="printer-pdf-btn">
                  <Printer size={14} weight="bold" /> Printer PDF (90×50mm per page)
                </button>
                <button onClick={() => exportCsv("codes")} className="label-tag px-4 py-3 border border-black brutal-btn flex items-center gap-2" data-testid="export-codes-csv">
                  <FileCsv size={14} weight="bold" /> Export CSV
                </button>
                <button onClick={() => window.open("/admin/brand-qr", "_blank")} className="label-tag px-4 py-3 border border-black brutal-btn flex items-center gap-2" data-testid="brand-qr-btn">
                  <Printer size={14} weight="bold" /> Brand QR (curbcrew.co.za)
                </button>
                <div className="label-tag text-neutral-600">Select unassigned codes below for targeted print</div>
              </div>
            </div>

            <div className="overflow-x-auto brutal-border bg-white" data-testid="codes-table">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left label-tag p-3">Select</th>
                    <th className="text-left label-tag p-3">Code</th>
                    <th className="text-left label-tag p-3">Batch</th>
                    <th className="text-left label-tag p-3">Status</th>
                    <th className="text-left label-tag p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCodes.map((c) => (
                      <tr key={c.id} className="border-b border-neutral-200" data-testid={`code-row-${c.code}`}>
                        <td className="p-3">
                          {c.status === "unassigned" ? (
                            <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} data-testid={`select-${c.code}`} />
                          ) : null}
                        </td>
                        <td className="p-3 font-mono-cc">{c.code}</td>
                        <td className="p-3 text-xs text-neutral-600 font-mono-cc max-w-[200px] truncate" title={c.batch_name || ""}>{c.batch_name || "—"}</td>
                        <td className="p-3">
                          <span className={`label-tag px-2 py-1 border border-black ${c.status === "activated" ? "bg-black text-white" : c.status === "assigned" ? "bg-[color:var(--cc-yellow)]" : "bg-white"}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="p-3">
                          <button onClick={() => setViewQr(c.code)} className="label-tag px-3 py-1 border border-black brutal-btn" data-testid={`admin-qr-${c.code}`}>
                            <QrCode size={14} weight="bold" className="inline mr-1" /> QR
                          </button>
                        </td>
                      </tr>
                  ))}
                  {filteredCodes.length === 0 && (
                    <tr><td className="p-4 text-neutral-500" colSpan={5}>No codes match this filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="label-tag text-neutral-600">
              {batchFilter ? <>Showing batch: <strong>{batchFilter}</strong> · </> : null}
              {filteredCodes.filter((c) => c.status === "unassigned").length} unassigned · {filteredCodes.length} total
            </div>
          </div>
        )}

        {tab === "cardholders" && (
          <div>
            {orphanUsers.length > 0 && (
              <div className="mb-8" data-testid="orphan-users-section">
                <div className="mb-3 flex items-end justify-between gap-4">
                  <div>
                    <div className="label-tag text-neutral-500">Registered, no card</div>
                    <div className="font-display font-black text-xl">Accounts waiting to activate</div>
                    <div className="text-sm text-neutral-600 mt-1">Signed up via /register but haven’t linked a physical card yet.</div>
                  </div>
                  <span className="label-tag px-2 py-1 border border-black bg-[color:var(--cc-yellow)]">{orphanUsers.length}</span>
                </div>
                <div className="overflow-x-auto brutal-border bg-white" data-testid="orphan-users-table">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b-2 border-black">
                        <th className="text-left label-tag p-3">Name</th>
                        <th className="text-left label-tag p-3">Email</th>
                        <th className="text-left label-tag p-3">Phone</th>
                        <th className="text-left label-tag p-3">Registered</th>
                        <th className="text-left label-tag p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphanUsers.map((u) => (
                        <tr key={u.id} className="border-b border-neutral-200" data-testid={`orphan-row-${u.id}`}>
                          <td className="p-3 font-semibold">{u.name || "—"}</td>
                          <td className="p-3 font-mono-cc text-sm whitespace-nowrap" title={u.email}>{u.email}</td>
                          <td className="p-3 font-mono-cc text-sm">{u.phone || "—"}</td>
                          <td className="p-3 text-sm text-neutral-600">{u.created_at ? timeAgo(u.created_at) : "—"}</td>
                          <td className="p-3 whitespace-nowrap">
                            <button onClick={() => openRecoverOrphan(u)} className="label-tag px-3 py-1 border border-black brutal-btn" data-testid={`orphan-recover-${u.id}`}>
                              <Lifebuoy size={14} weight="bold" className="inline mr-1" /> Recover
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mb-4 flex items-end justify-between gap-4">
              <div>
                <div className="label-tag text-neutral-500">Activated cards</div>
                <div className="font-display font-black text-xl">Cardholders</div>
              </div>
              <button onClick={() => exportCsv("cardholders")} className="label-tag px-4 py-2 border border-black brutal-btn flex items-center gap-2" data-testid="export-cardholders-csv">
                <FileCsv size={14} weight="bold" /> Export cardholders CSV
              </button>
            </div>
            <div className="overflow-x-auto brutal-border bg-white" data-testid="cardholders-table">
            <table className="min-w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left label-tag p-3">Photo</th>
                  <th className="text-left label-tag p-3">Code</th>
                  <th className="text-left label-tag p-3">Name</th>
                  <th className="text-left label-tag p-3">Email / login</th>
                  <th className="text-left label-tag p-3">Skills</th>
                  <th className="text-left label-tag p-3">Status</th>
                  <th className="text-left label-tag p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {cardholders.map((c) => {
                  const lost = c.lost_reported || c.is_active === false;
                  return (
                    <tr key={c.id} className="border-b border-neutral-200" data-testid={`cardholder-row-${c.code}`}>
                      <td className="p-3">
                        <div className="w-12 h-12 border border-black bg-[color:var(--cc-bg-2)] overflow-hidden">
                          {c.picture_url && <img src={`${BACKEND}${c.picture_url}`} alt="" className="w-full h-full object-cover" />}
                        </div>
                      </td>
                      <td className="p-3 font-mono-cc">{c.code}</td>
                      <td className="p-3 font-semibold">{c.full_name}</td>
                      <td className="p-3 text-sm whitespace-nowrap">
                        {c.has_login ? (
                          <span className="font-mono-cc text-xs" title={c.owner_email}>{c.owner_email}</span>
                        ) : (
                          <span className="label-tag px-2 py-1 border border-black bg-[color:var(--cc-yellow)]">No login</span>
                        )}
                      </td>
                      <td className="p-3 text-sm">{(c.skills || []).join(", ")}</td>
                      <td className="p-3">
                        <span className={`label-tag px-2 py-1 border border-black ${lost ? "bg-[color:var(--cc-yellow)]" : "bg-black text-white"}`}>
                          {lost ? "Lost / inactive" : "Active"}
                        </span>
                      </td>
                      <td className="p-3 space-x-2 whitespace-nowrap">
                        <button onClick={() => setViewQr(c.code)} className="label-tag px-3 py-1 border border-black brutal-btn" data-testid={`ch-qr-${c.code}`}>
                          <QrCode size={14} weight="bold" className="inline mr-1" /> QR
                        </button>
                        <button onClick={() => openEditCh(c)} className="label-tag px-3 py-1 bg-[color:var(--cc-yellow)] border border-black brutal-btn" data-testid={`ch-edit-${c.code}`}>
                          <PencilSimple size={14} weight="bold" className="inline mr-1" /> Edit
                        </button>
                        <button onClick={() => openRecover(c)} className="label-tag px-3 py-1 border border-black brutal-btn" data-testid={`ch-recover-${c.code}`}>
                          <Lifebuoy size={14} weight="bold" className="inline mr-1" /> Recover
                        </button>
                        {!lost ? (
                          <button onClick={() => toggleActive(c, true)} className="label-tag px-3 py-1 border border-black brutal-btn" data-testid={`ch-deactivate-${c.code}`}>
                            <Flag size={14} weight="bold" className="inline mr-1" /> Deactivate
                          </button>
                        ) : (
                          <button onClick={() => toggleActive(c, false)} className="label-tag px-3 py-1 bg-[color:var(--cc-yellow)] border border-black brutal-btn" data-testid={`ch-restore-${c.code}`}>
                            <ArrowCounterClockwise size={14} weight="bold" className="inline mr-1" /> Restore
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {cardholders.length === 0 && <tr><td colSpan={7} className="p-4 text-neutral-500">No cardholders yet.</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div>
            <div className="mb-4 flex justify-end">
              <button onClick={() => exportCsv("leads")} className="label-tag px-4 py-2 border border-black brutal-btn flex items-center gap-2" data-testid="export-leads-csv">
                <FileCsv size={14} weight="bold" /> Export leads CSV
              </button>
            </div>
            <div className="overflow-x-auto brutal-border bg-white" data-testid="leads-table">            <table className="min-w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left label-tag p-3">Done</th>
                  <th className="text-left label-tag p-3">When</th>
                  <th className="text-left label-tag p-3">Cardholder</th>
                  <th className="text-left label-tag p-3">Interested</th>
                  <th className="text-left label-tag p-3">Phone</th>
                  <th className="text-left label-tag p-3">Message</th>
                  <th className="text-left label-tag p-3">Forward</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className={`border-b border-neutral-200 ${l.contacted ? "bg-neutral-50 text-neutral-500" : ""}`}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="p-3">
                      <button
                        onClick={() => toggleContacted(l.id)}
                        className="p-1 hover:bg-neutral-100 rounded"
                        title={l.contacted ? "Mark as pending again" : "Mark as contacted"}
                        data-testid={`contacted-toggle-${l.id}`}
                      >
                        {l.contacted
                          ? <CheckCircle size={24} weight="fill" className="text-green-600" />
                          : <Circle size={24} weight="regular" className="text-neutral-400" />}
                      </button>
                    </td>
                    <td className="p-3 text-xs font-mono-cc">{new Date(l.created_at).toLocaleString()}</td>
                    <td className={`p-3 ${l.contacted ? "line-through" : ""}`}>{l.cardholder_name} <span className="font-mono-cc text-xs text-neutral-500">#{l.code}</span></td>
                    <td className={`p-3 font-semibold ${l.contacted ? "line-through" : ""}`}>{l.interested_name}</td>
                    <td className="p-3 font-mono-cc">{l.interested_phone}</td>
                    <td className="p-3 text-sm text-neutral-700">{l.message || "—"}</td>
                    <td className="p-3">
                      {l.cardholder_phone ? (
                        <button
                          onClick={() => forwardLeadWhatsApp(l)}
                          className="label-tag px-3 py-1 border border-black brutal-btn flex items-center gap-1 bg-[#25D366] text-black"
                          title={`Forward to ${l.cardholder_name} on WhatsApp`}
                          data-testid={`forward-wa-${l.id}`}
                        >
                          <WhatsappLogo size={14} weight="bold" /> WhatsApp
                        </button>
                      ) : (
                        <span className="label-tag text-neutral-400">No phone</span>
                      )}
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan={7} className="p-4 text-neutral-500">No leads yet.</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {tab === "scans" && (
          <div className="space-y-6" data-testid="scans-panel">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[color:var(--cc-yellow)] border border-black p-5">
                <div className="label-tag">Total scans</div>
                <div className="font-display font-black text-4xl mt-2" data-testid="scans-total">{scanTotal}</div>
                <div className="label-tag text-neutral-700 mt-1">across all cards · all time</div>
              </div>
              <div className="bg-white border border-black p-5">
                <div className="label-tag">Cities tracked</div>
                <div className="font-display font-black text-4xl mt-2">{scanSummary.filter(s => s.city).length}</div>
                <div className="label-tag text-neutral-600 mt-1">geo-located scans only</div>
              </div>
              <div className="bg-white border border-black p-5 flex flex-col gap-2">
                <div className="label-tag">Export</div>
                <button
                  onClick={() => downloadCsv("curbcrew-scans.csv", scanEvents.map(e => ({
                    when: e.created_at, code: e.code, city: e.city, region: e.region, country: e.country, user_agent: e.user_agent, referer: e.referer,
                  })))}
                  className="label-tag px-3 py-2 border border-black brutal-btn flex items-center gap-2 self-start"
                  data-testid="scans-export-btn"
                >
                  <FileCsv size={14} weight="bold" /> Download recent scans CSV
                </button>
                <button onClick={loadScans} className="label-tag px-3 py-2 border border-black brutal-btn flex items-center gap-2 self-start">
                  <ArrowCounterClockwise size={14} weight="bold" /> Refresh
                </button>
              </div>
            </div>

            <div>
              <div className="label-tag mb-2 flex items-center gap-2"><MapPin size={14} weight="bold" /> Scan map</div>
              <ScanMap summary={scanSummary} />
            </div>

            <div>
              <div className="label-tag mb-2 flex items-center gap-2"><MapPin size={14} weight="bold" /> Top locations</div>
              <div className="overflow-x-auto brutal-border bg-white" data-testid="scans-summary-table">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b-2 border-black">
                      <th className="text-left label-tag p-3">City</th>
                      <th className="text-left label-tag p-3">Region</th>
                      <th className="text-left label-tag p-3">Country</th>
                      <th className="text-right label-tag p-3">Scans</th>
                      <th className="text-left label-tag p-3">Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanSummary.map((s, i) => (
                      <tr key={i} className="border-b border-neutral-200" data-testid={`scan-summary-row-${i}`}>
                        <td className="p-3 font-semibold">{s.city || <span className="text-neutral-400">— unknown —</span>}</td>
                        <td className="p-3 text-sm text-neutral-700">{s.region || "—"}</td>
                        <td className="p-3 text-sm text-neutral-700">{s.country || "—"}</td>
                        <td className="p-3 text-right font-mono-cc font-semibold">{s.scans}</td>
                        <td className="p-3 text-xs font-mono-cc text-neutral-500">{s.last_seen ? new Date(s.last_seen).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                    {scanSummary.length === 0 && <tr><td colSpan={5} className="p-4 text-neutral-500">No scans logged yet. Public profile views will show up here.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="label-tag mb-2">Recent scans (last 200)</div>
              <div className="overflow-x-auto brutal-border bg-white" data-testid="scan-events-table">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b-2 border-black">
                      <th className="text-left label-tag p-3">When</th>
                      <th className="text-left label-tag p-3">Card</th>
                      <th className="text-left label-tag p-3">Where</th>
                      <th className="text-left label-tag p-3">Device</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanEvents.map((e) => (
                      <tr key={e.id} className="border-b border-neutral-200" data-testid={`scan-event-${e.id}`}>
                        <td className="p-3 text-xs font-mono-cc whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                        <td className="p-3 font-mono-cc">{e.code}</td>
                        <td className="p-3 text-sm">
                          {[e.city, e.region, e.country].filter(Boolean).join(", ") || <span className="text-neutral-400">unknown</span>}
                        </td>
                        <td className="p-3 text-xs text-neutral-600 max-w-[280px] truncate" title={e.user_agent}>{e.user_agent || "—"}</td>
                      </tr>
                    ))}
                    {scanEvents.length === 0 && <tr><td colSpan={4} className="p-4 text-neutral-500">No scans yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "lost" && (
          <div className="overflow-x-auto brutal-border bg-white" data-testid="lost-reports-table">
            <table className="min-w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left label-tag p-3">When</th>
                  <th className="text-left label-tag p-3">Card</th>
                  <th className="text-left label-tag p-3">Cardholder</th>
                  <th className="text-left label-tag p-3">Reporter</th>
                  <th className="text-left label-tag p-3">Phone</th>
                  <th className="text-left label-tag p-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {lostReports.map((r) => (
                  <tr key={r.id} className="border-b border-neutral-200" data-testid={`lost-row-${r.id}`}>
                    <td className="p-3 text-xs font-mono-cc">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="p-3 font-mono-cc">{r.code}</td>
                    <td className="p-3 font-semibold">{r.cardholder_name}</td>
                    <td className="p-3">{r.reporter_name || "—"}</td>
                    <td className="p-3 font-mono-cc">{r.reporter_phone || "—"}</td>
                    <td className="p-3 text-sm text-neutral-700">{r.note || "—"}</td>
                  </tr>
                ))}
                {lostReports.length === 0 && <tr><td colSpan={6} className="p-4 text-neutral-500">No lost reports yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Recover cardholder / orphan-user modal */}
      {(recoverCh || recoverOrphan) && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="recover-modal">
          <div className="bg-white border border-black max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-black p-4">
              <div>
                <div className="label-tag text-neutral-500 flex items-center gap-1">
                  <Lifebuoy size={12} weight="bold" /> {recoverOrphan ? "Recover registered user" : "Recover cardholder"}
                </div>
                <div className="font-display font-black text-xl">
                  {recoverOrphan ? (recoverOrphan.name || recoverOrphan.email) : recoverCh.full_name}
                </div>
                {recoverCh && <div className="font-mono-cc text-xs text-neutral-500">#{recoverCh.code}</div>}
                {recoverOrphan && <div className="label-tag text-neutral-500 mt-1">No card linked yet</div>}
              </div>
              <button onClick={closeRecover} className="p-2"><X size={20} weight="bold" /></button>
            </div>
            <div className="p-4 space-y-4">
              {!recoverAccount && <div className="label-tag text-neutral-500">Loading account…</div>}
              {recoverAccount && !recoverAccount.account && (
                <div className="bg-[color:var(--cc-yellow)] border border-black p-3 text-sm">
                  {recoverAccount.reason || "No login account exists for this cardholder."}
                </div>
              )}
              {recoverAccount?.account && (
                <>
                  <div className="bg-neutral-100 border border-black p-3 text-sm">
                    <div className="label-tag text-neutral-500">Current email on file</div>
                    <div className="font-mono-cc text-sm mt-1 break-all" data-testid="recover-current-email">{recoverAccount.account.email}</div>
                    <div className="label-tag text-neutral-500 mt-2">Phone on file</div>
                    <div className="font-mono-cc text-sm mt-1">
                      {recoverOrphan ? (recoverOrphan.phone || "—") : (recoverCh.phone || "—")}
                    </div>
                  </div>

                  <div className="border-t border-dashed border-neutral-400 pt-3">
                    <label className="label-tag">Change email (if they want to switch to a new one)</label>
                    <input
                      type="email"
                      className="mt-1 w-full px-3 py-2 border border-black outline-none font-mono-cc text-sm"
                      value={recoverNewEmail}
                      onChange={(e) => setRecoverNewEmail(e.target.value)}
                      data-testid="recover-new-email"
                    />
                    <button onClick={saveRecoverEmail} className="mt-2 label-tag px-3 py-2 bg-[color:var(--cc-yellow)] border border-black brutal-btn" data-testid="recover-save-email">
                      Save new email
                    </button>
                  </div>

                  <div className="border-t border-dashed border-neutral-400 pt-3">
                    <label className="label-tag">Reset password to</label>
                    <input
                      type="text"
                      className="mt-1 w-full px-3 py-2 border border-black outline-none font-mono-cc text-sm"
                      value={recoverNewPw}
                      onChange={(e) => setRecoverNewPw(e.target.value)}
                      placeholder="Type a temp password (6+ chars)"
                      data-testid="recover-new-password"
                    />
                    <button onClick={saveRecoverPassword} className="mt-2 label-tag px-3 py-2 bg-black text-white brutal-btn" data-testid="recover-save-password">
                      Reset password
                    </button>
                  </div>

                  {recoverMsg && (
                    <div className={`label-tag p-3 border border-black ${recoverMsg.startsWith("✓") ? "bg-green-100" : "bg-red-100 text-red-700"}`} data-testid="recover-msg">
                      {recoverMsg}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {viewQr && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="admin-qr-modal">
          <div className="bg-white border border-black max-w-md w-full">
            <div className="flex items-center justify-between border-b border-black p-4">
              <div className="font-display font-black text-xl">QR for #{viewQr}</div>
              <button onClick={() => setViewQr(null)} className="p-2"><X size={20} weight="bold" /></button>
            </div>
            <div className="p-6 flex flex-col items-center gap-4">
              <div className="bg-white border border-black p-4">
                <QRCodeSVG value={`${FRONTEND_BASE}/c/${viewQr}`} size={240} level="H" />
                <div className="mt-2 font-mono-cc text-xs text-center">{viewQr}</div>
              </div>
              <div className="label-tag text-center text-neutral-600 break-all">{FRONTEND_BASE}/c/{viewQr}</div>
              <div className="flex gap-2">
                <button onClick={() => navigator.clipboard.writeText(`${FRONTEND_BASE}/c/${viewQr}`)} className="label-tag px-3 py-2 border border-black brutal-btn flex items-center gap-2">
                  <Copy size={14} weight="bold" /> Copy link
                </button>
                <a href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(`${FRONTEND_BASE}/c/${viewQr}`)}`} target="_blank" rel="noreferrer" className="label-tag px-3 py-2 bg-black text-white brutal-btn flex items-center gap-2">
                  <Download size={14} weight="bold" /> PNG
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Factory reset modal */}
      {showReset && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="factory-reset-modal">
          <div className="bg-white border-2 border-red-600 max-w-md w-full">
            <div className="flex items-center justify-between border-b-2 border-red-600 p-4 bg-red-600 text-white">
              <div>
                <div className="label-tag">Danger</div>
                <div className="font-display font-black text-xl">Reset all data</div>
              </div>
              <button onClick={() => { setShowReset(false); setResetConfirm(""); }} className="p-2"><X size={20} weight="bold" /></button>
            </div>
            <form onSubmit={submitFactoryReset} className="p-4 space-y-3">
              <p className="text-sm text-neutral-800 leading-relaxed">
                This will <strong>permanently delete</strong> every code, cardholder, lead and lost report
                in this database. Your admin login will be kept. <strong>This cannot be undone.</strong>
              </p>
              <div>
                <label className="label-tag">To confirm, type <span className="font-mono-cc bg-neutral-100 px-2 py-0.5">RESET CURBCREW</span> below</label>
                <input
                  type="text"
                  value={resetConfirm}
                  onChange={(e) => setResetConfirm(e.target.value)}
                  placeholder="RESET CURBCREW"
                  className="mt-1 w-full px-4 py-3 border border-black outline-none font-mono-cc"
                  data-testid="factory-reset-input"
                  autoFocus
                />
              </div>
              {err && <div className="label-tag text-red-600">{err}</div>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={resetConfirm !== "RESET CURBCREW"}
                  className="bg-red-600 text-white label-tag px-4 py-3 brutal-btn flex-1 disabled:opacity-50"
                  data-testid="factory-reset-confirm"
                >
                  Yes — wipe everything
                </button>
                <button type="button" onClick={() => { setShowReset(false); setResetConfirm(""); }} className="label-tag px-4 py-3 border border-black brutal-btn">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit cardholder modal */}
      {editCh && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="admin-edit-ch-modal">
          <div className="bg-white border border-black max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-black p-4">
              <div>
                <div className="label-tag text-neutral-500">Editing cardholder</div>
                <div className="font-display font-black text-xl">#{editCh.code} · {editCh.full_name}</div>
              </div>
              <button onClick={() => setEditCh(null)} className="p-2"><X size={20} weight="bold" /></button>
            </div>
            <form onSubmit={submitEditCh} className="p-4 space-y-3">
              <div>
                <label className="label-tag">Full name</label>
                <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} data-testid="admin-edit-name" required />
              </div>
              <div>
                <label className="label-tag">Skills (comma-separated)</label>
                <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={editForm.skills} onChange={(e) => setEditForm({ ...editForm, skills: e.target.value })} data-testid="admin-edit-skills" required />
              </div>
              <div>
                <label className="label-tag">SA phone</label>
                <input className="mt-1 w-full px-4 py-3 border border-black outline-none font-mono-cc" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value.replace(/[^0-9]/g, "") })} maxLength={10} inputMode="numeric" data-testid="admin-edit-phone" required />
              </div>
              <div>
                <label className="label-tag">Location</label>
                <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} data-testid="admin-edit-location" />
              </div>
              <div>
                <label className="label-tag">Bio</label>
                <textarea rows={3} className="mt-1 w-full px-4 py-3 border border-black outline-none" value={editForm.bio} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} data-testid="admin-edit-bio" />
              </div>
              <div>
                <label className="label-tag">Photo</label>
                <div className="mt-2 flex items-start gap-3">
                  <div className="w-24 h-24 border border-black bg-[color:var(--cc-bg-2)] grid place-items-center overflow-hidden flex-shrink-0">
                    {editForm.picture_url ? (
                      <img src={`${BACKEND}${editForm.picture_url}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Camera size={28} weight="bold" className="text-neutral-400" />
                    )}
                  </div>
                  <label className="label-tag px-4 py-3 border border-black brutal-btn cursor-pointer inline-flex items-center gap-2">
                    <Camera size={14} weight="bold" />
                    {uploadingPhoto ? "Uploading…" : "Replace photo"}
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleAdminPhotoUpload(e.target.files?.[0])} data-testid="admin-edit-photo-input" />
                  </label>
                </div>
              </div>
              {err && <div className="label-tag text-red-600">{err}</div>}
              <div className="flex gap-2">
                <button type="submit" className="bg-black text-white label-tag px-4 py-3 brutal-btn flex-1" data-testid="admin-edit-submit">
                  Save changes
                </button>
                <button type="button" onClick={() => setEditCh(null)} className="label-tag px-4 py-3 border border-black brutal-btn">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
